import { MoCommand } from "./command";
import { MoveToObjCmd } from "./movetoobj";
import { ITEMLOC, INVEQUIPIDX, VISEQUIPIDX } from "../../gamedata/inventorydata";

export class _AttackCmd extends MoCommand {
    pawn: any;
    target: any;
    elapsedMs = 0;
    motionFinished = false;
    cooldownMs = 2000;
    active = false;
    impactSent = false;

    constructor(object: any, targetObjRef: any) {
        super(object);
        this.pawn = this.object.pawn;
        this.target = targetObjRef;
    }

    enter() {
        this.active = true;
        this.cooldownMs = this.object.stats.getAttackCooldownMs?.() || 2000;
        // Update the authoritative position after MoveToObjCmd reaches melee
        // range. The attack request itself is sent at the motion's impact
        // marker so the damage packet arrives with the visible weapon strike.
        if (this.object.type === "local") {
            netGame.syncPosition(this.object.position.x, this.object.position.y, this.object.position.z);
        }

        const weapon = this.object.type === "local"
            ? this.object.inventory.findByLocSlot(ITEMLOC.EQUIPPED_EQUIP, INVEQUIPIDX.WEAPON)
            : this.object.visParts?.[VISEQUIPIDX.WEAPON];

        if (weapon && weapon.itemNo) {
            this.pawn.playAttackMotion(this._attackDone.bind(this), this._attackImpact.bind(this));
        } else {
            this.pawn.playBareHandsAttackMotion(this._attackDone.bind(this), this._attackImpact.bind(this));
        }
    }

    _attackImpact() {
        if (!this.active || this.impactSent || this.object.type !== "local") return;
        this.impactSent = true;
        netGame.attackObj(this.target.serverObjectIdx);
    }

    _attackDone() {
        if (!this.active) return;
        this.motionFinished = true;
        this.pawn.playIdleMotion?.();
        this.finishIfReady();
    }

    finishIfReady() {
        const targetDied = this.target.object?.isDead === true;
        if (
            this.active &&
            !this.isComplete &&
            this.motionFinished &&
            (this.elapsedMs >= this.cooldownMs || targetDied)
        ) {
            this.isComplete = true;
            this.emit("finish");
        }
    }

    leave() {
        this.active = false;
    }

    update(delta: number) {
        this.elapsedMs += delta * 1000;
        this.finishIfReady();
        return 0;
    }
}

export class AttackCmd extends MoCommand {
    target: any;
    activeCmd: any;

    constructor(object: any, targetObjRef: any) {
        super(object);
        this.target = targetObjRef;
        this.activeCmd = null;
    }

    enter() {
        this._goOnce();
    }

    leave() {
        if (this.activeCmd) {
            this.activeCmd._leave();
            this.activeCmd = null;
        }
    }

    _goOnce() {
        if (!this.target.object || this.target.object.isDead) {
            this.isComplete = true;
            return;
        }

        // Interupt here, as you cannot interupt in the middle of attack animation.
        if (this.wantInterrupt) {
            this.isComplete = true;
            return;
        }

        this.activeCmd = new MoveToObjCmd(
            this.object,
            this.target,
            this.object.stats.getAttackDistance()
        );
        this.activeCmd.on(
            "finish",
            function () {
                if (!this.activeCmd) {
                    return;
                }
                this.activeCmd._leave();
                if (!this.target.object || this.target.object.isDead) {
                    this.activeCmd = null;
                    this.isComplete = true;
                    return;
                }
                this.activeCmd = new _AttackCmd(this.object, this.target);
                this.activeCmd.on(
                    "finish",
                    function () {
                        if (!this.activeCmd) {
                            return;
                        }
                        this.activeCmd._leave();
                        this.activeCmd = null;
                        this._goOnce();
                    }.bind(this)
                );
                this.activeCmd._enter();
            }.bind(this)
        );
        this.activeCmd._enter();
    }

    update(delta: number) {
        if (this.wantInterrupt && !(this.activeCmd instanceof _AttackCmd)) {
            this.isComplete = true;
            return delta;
        }

        // A lethal hit marks the target dead before its death animation removes it.
        if (!this.target.object || this.target.object.isDead) {
            // A lethal damage packet normally arrives at the swing's impact
            // marker. Let that already-visible swing reach its own finish
            // event, then stop instead of snapping the player to idle here.
            if (this.activeCmd instanceof _AttackCmd) {
                return this.activeCmd.update(delta);
            }
            if (this.activeCmd) {
                this.activeCmd._leave();
                this.activeCmd = null;
            }
            this.isComplete = true;
            return delta;
        }

        if (!this.activeCmd) {
            // This is really an error condition, we eat the delta to make sure
            //   we don't loop forever in the caller loop.
            return 0;
        }

        return this.activeCmd.update(delta);
    }
}
